/**
 * ReviewPermissionsRenderer - Renderer for review_permissions onboarding action.
 *
 * Shows toggles for Gmail and Google Calendar data visibility settings with a privacy banner.
 * On toggle change, syncs updated preferences to the action payload via onUpdate.
 * Actual CRM event submission happens server-side when the proposal is approved.
 */

import { Shield01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGmailStatus } from '@/hooks/integrations/google/useGmailStatus';
import { useOutlookStatus } from '@/hooks/integrations/microsoft/useOutlookStatus';
import { Badge } from '@/lib/base/badges/badges';
import { Toggle } from '@/lib/base/forms/toggle';
import { logger } from '@/lib/logger';
import type {
  ActionPayload,
  OnboardingActionPayload,
  ProposedAction,
} from '@/lib/proposals/core/types';

import { createProposalClient } from '../../../lib/proposals/core/ProposalClient';
import type { ActionRendererProps } from '../../../lib/proposals/registry/types';
import {
  ProposalContainer,
  ProposalInfoCallout,
  useCoordinatedAutoSave,
} from '../shared';

/**
 * Privacy banner component explaining data visibility.
 */
function PrivacyBanner() {
  return (
    <ProposalInfoCallout icon={Shield01}>
      <p className='font-semibold mb-1'>Choose your data visibility settings</p>
      <p>
        These settings are enabled by default to help your team collaborate. You
        can disable them anytime.
      </p>
    </ProposalInfoCallout>
  );
}

/**
 * Toggle setting row with numbered badge.
 */
interface ToggleSettingProps {
  number: number;
  label: string;
  hint: string;
  isSelected: boolean;
  onChange: (value: boolean) => void;
  isDisabled: boolean;
}

function ToggleSetting({
  number,
  label,
  hint,
  isSelected,
  onChange,
  isDisabled,
}: ToggleSettingProps) {
  return (
    <div className='flex items-center gap-3 py-3'>
      <Badge type='pill-color' color='gray' size='sm' className='flex-shrink-0'>
        {number}
      </Badge>
      <Toggle
        isSelected={isSelected}
        onChange={onChange}
        isDisabled={isDisabled}
        size='sm'
        className='flex-shrink-0'
      />
      <div className='min-w-0'>
        <p className='text-sm font-medium text-secondary'>{label}</p>
        <span className='text-sm text-tertiary'>{hint}</span>
      </div>
    </div>
  );
}

/**
 * Renderer for review_permissions onboarding action.
 *
 * Syncs toggle state to the action payload via onUpdate so the backend executor
 * can read the preferences when the proposal is approved via StickyFooter.
 */
export function ReviewPermissionsRenderer({
  action,
  mode,
  className = '',
  orgId,
  onUpdate,
  proposalId,
}: ActionRendererProps) {
  // Fetch current Gmail and Outlook status to get entity IDs and visibility values
  const orgIdStr = orgId !== undefined ? String(orgId) : undefined;
  const { data: gmailStatus } = useGmailStatus(orgIdStr);
  const { data: outlookStatus } = useOutlookStatus(orgIdStr);

  // Get primary Google account data
  const primaryAccount = gmailStatus?.accounts?.find(acc => acc.is_primary);
  const gmailEntityId = primaryAccount?.gmail_entity_id;
  const gcalEntityId = primaryAccount?.gcal_entity_id;

  // Get first connected (non-orphan) Outlook account. social_account_id is null
  // for orphaned accounts (SocialAccount removed, CRM entity remains), so
  // selecting by array order could bind toggles/payload to an orphan.
  const outlookAccount = outlookStatus?.accounts?.find(
    acc => acc.social_account_id !== null
  );
  const outlookMailEntityId = outlookAccount?.outlook_mail_entity_id;
  const outlookCalEntityId = outlookAccount?.outlook_calendar_entity_id;

  // Initialize toggle state (will sync with data when it arrives)
  const [emailVisibility, setEmailVisibility] = useState(true);
  const [calendarVisibility, setCalendarVisibility] = useState(true);
  const [syncedAccountId, setSyncedAccountId] = useState<number | null>(null);

  // Create proposal client for persisting payload updates to backend
  const proposalClient = useMemo(
    () => (orgId ? createProposalClient(orgId) : null),
    [orgId]
  );

  // Each successful update_payload past the versioning window mints a NEW
  // action version (new id) and marks the old row `is_current_version=False`;
  // the backend's `get_action` only resolves current versions, so reusing the
  // old id makes the NEXT save fail with "Action {id} not found". Track the
  // current id and adopt the server's returned action after every save.
  const actionIdRef = useRef(action.id);
  useEffect(() => {
    actionIdRef.current = action.id;
  }, [action.id]);

  // Persist the payload to the backend. Rejecting marks the save errored, which
  // is what lets the approve gate's flush block on a write that never landed.
  const persistPayload = useCallback(
    async (updatedPayload: OnboardingActionPayload) => {
      if (!proposalClient || proposalId === undefined) {
        return;
      }
      const saved = await proposalClient.updateActionPayload(
        proposalId,
        actionIdRef.current,
        { payload: updatedPayload as unknown as ActionPayload }
      );
      // Adopt the server's row so the next save targets the current version.
      actionIdRef.current = saved.id;
      onUpdate?.(saved);
    },
    [proposalClient, proposalId, onUpdate]
  );

  // The shared coordinated autosave owns the three things this card used to
  // hand-roll — and the one it omitted entirely:
  //   1. Registration with AutoSaveCoordination, so StickyFooter's
  //      `flushAllAndWait()` waits for an in-flight toggle write. Without it the
  //      approve dispatched mid-write, snapshotted a pre-toggle payload, and the
  //      backend rejected it (409 STALE_ACTIONS on an in-place overwrite, 500 on
  //      a version bump).
  //   2. Freeze checks at both arm and fire time, so no write is issued after
  //      approval starts — those return UPDATE_FAILED once the proposal leaves
  //      `pending`, which is what the old retry loop burned its attempts on.
  //   3. Change detection, so an unchanged payload is not rewritten — the
  //      mount-time sync no longer mints a version per card open.
  // Keyed on the proposal, not the action: a save can mint a new action id, and
  // re-registering mid-flight would drop the flush the approve gate needs.
  const { save: queueVisibilitySave } =
    useCoordinatedAutoSave<OnboardingActionPayload>({
      registrationId: `review-permissions-${proposalId ?? 'unscoped'}`,
      onSave: persistPayload,
      // Deliberately unseeded. The mount sync writes the account's live
      // visibility and the timeline entity ids into the payload, and that write
      // must happen even when it matches the stored payload field-for-field —
      // the executor reads those ids, so seeding change detection here would
      // suppress the one write the card exists to make.
      enabled: proposalClient !== null && proposalId !== undefined,
      // A toggle is a discrete commit, not typing — persist it straight away and
      // keep the pre-existing save-on-toggle behaviour. The debounce is what the
      // text editors need; the coordination wiring is what this card was missing.
      debounceMs: 0,
    });

  // Helper to notify parent of payload changes AND persist to backend
  const syncPayload = useCallback(
    (
      emailEnabled: boolean,
      calEnabled: boolean,
      gmailId?: string,
      gcalId?: string,
      outlookMailId?: string,
      outlookCalId?: string
    ) => {
      const currentPayload = action.payload as OnboardingActionPayload;
      const updatedPayload: OnboardingActionPayload = {
        ...currentPayload,
        action_params: {
          ...currentPayload.action_params,
          email_visibility_enabled: emailEnabled,
          calendar_visibility_enabled: calEnabled,
          ...(gmailId ? { gmail_timeline_id: gmailId } : {}),
          ...(gcalId ? { gcal_timeline_id: gcalId } : {}),
          ...(outlookMailId ? { outlook_mail_timeline_id: outlookMailId } : {}),
          ...(outlookCalId
            ? { outlook_calendar_timeline_id: outlookCalId }
            : {}),
        },
      };

      // Optimistic UI update
      if (onUpdate) {
        const updatedAction: ProposedAction = {
          ...action,
          payload: updatedPayload,
        };
        onUpdate(updatedAction);
      }

      // Arm the coordinated save; the approve gate flushes it before dispatching
      queueVisibilitySave(updatedPayload);
    },
    [action, onUpdate, queueVisibilitySave]
  );

  // Sync toggle state when account data becomes available.
  // Reads visibility from the primary Google account (or first Outlook account
  // when no Google account is connected) so the toggles default to the user's
  // current settings.
  useEffect(() => {
    const sourceAccount = primaryAccount ?? outlookAccount;
    if (!sourceAccount) {
      return;
    }
    // Only dedupe on a real (non-null) id. syncedAccountId starts as null, so a
    // null source id would otherwise match and skip the initial sync.
    if (
      sourceAccount.social_account_id !== null &&
      sourceAccount.social_account_id === syncedAccountId
    ) {
      return;
    }

    // Determine initial visibility from whichever provider is available
    let emailEnabled = true;
    let calEnabled = true;
    if (primaryAccount) {
      emailEnabled = primaryAccount.gmail_default_data_visibility !== 'private';
      calEnabled = primaryAccount.gcal_default_data_visibility !== 'private';
    } else if (outlookAccount) {
      emailEnabled =
        outlookAccount.outlook_mail_default_data_visibility !== 'private';
      calEnabled =
        outlookAccount.outlook_calendar_default_data_visibility !== 'private';
    }

    setEmailVisibility(emailEnabled);
    setCalendarVisibility(calEnabled);
    setSyncedAccountId(sourceAccount.social_account_id);

    // Sync entity IDs and current visibility into the payload
    syncPayload(
      emailEnabled,
      calEnabled,
      gmailEntityId,
      gcalEntityId,
      outlookMailEntityId,
      outlookCalEntityId
    );
  }, [
    primaryAccount,
    outlookAccount,
    syncedAccountId,
    gmailEntityId,
    gcalEntityId,
    outlookMailEntityId,
    outlookCalEntityId,
    syncPayload,
  ]);

  // Handle toggle changes — update local state and sync to payload
  const handleEmailToggle = useCallback(
    (value: boolean) => {
      setEmailVisibility(value);
      syncPayload(
        value,
        calendarVisibility,
        gmailEntityId,
        gcalEntityId,
        outlookMailEntityId,
        outlookCalEntityId
      );
      logger.info('Email visibility toggled', {
        email_visibility: value ? 'org' : 'private',
        component: 'actions',
      });
    },
    [
      calendarVisibility,
      gmailEntityId,
      gcalEntityId,
      outlookMailEntityId,
      outlookCalEntityId,
      syncPayload,
    ]
  );

  const handleCalendarToggle = useCallback(
    (value: boolean) => {
      setCalendarVisibility(value);
      syncPayload(
        emailVisibility,
        value,
        gmailEntityId,
        gcalEntityId,
        outlookMailEntityId,
        outlookCalEntityId
      );
      logger.info('Calendar visibility toggled', {
        calendar_visibility: value ? 'org' : 'private',
        component: 'actions',
      });
    },
    [
      emailVisibility,
      gmailEntityId,
      gcalEntityId,
      outlookMailEntityId,
      outlookCalEntityId,
      syncPayload,
    ]
  );

  // Readonly mode - just show current settings
  if (mode === 'readonly') {
    return (
      <ProposalContainer className={className}>
        <PrivacyBanner />
        <div className='space-y-2'>
          <div className='flex items-center gap-2 text-sm'>
            <Badge type='pill-color' color='gray' size='sm'>
              1
            </Badge>
            <span className='text-fg-primary'>
              Show your emails on contact records:{' '}
              <strong>{emailVisibility ? 'On' : 'Off'}</strong>
            </span>
          </div>
          <div className='flex items-center gap-2 text-sm'>
            <Badge type='pill-color' color='gray' size='sm'>
              2
            </Badge>
            <span className='text-fg-primary'>
              Show your calendar events on contact records:{' '}
              <strong>{calendarVisibility ? 'On' : 'Off'}</strong>
            </span>
          </div>
        </div>
      </ProposalContainer>
    );
  }

  // Full mode - editable with toggles
  if (mode === 'full') {
    return (
      <ProposalContainer className={className}>
        <PrivacyBanner />

        {/* Toggle settings */}
        <div className='space-y-1'>
          <ToggleSetting
            number={1}
            label='Show your emails on contact records'
            hint='Your team members in this organization will see emails you send and receive with contacts'
            isSelected={emailVisibility}
            onChange={handleEmailToggle}
            isDisabled={false}
          />
          <ToggleSetting
            number={2}
            label='Show your calendar events on contact records'
            hint='Your team members in this organization will see your calendar events unless individually marked private'
            isSelected={calendarVisibility}
            onChange={handleCalendarToggle}
            isDisabled={false}
          />
        </div>
      </ProposalContainer>
    );
  }

  // Other modes not supported
  return (
    <div className={className}>
      <span className='text-sm text-fg-secondary'>
        Review permissions (mode: {mode})
      </span>
    </div>
  );
}
